import Icon from '@bugbug/core/components/Icon';
import mixins from '@bugbug/core/theme/mixins';
import styled, { css } from 'styled-components';

import { DragHandler as DragHandlerBase } from '~/components/DragHandler/DragHandler';
import EditableTextBase from '~/components/EditableText';
import { Expander } from '~/components/ExpanderButton/ExpanderButton.styled';

import { GroupActions as GroupActionsBase } from '../GroupActions/GroupActions';

export const AddIconWrapper = styled.div<{ bottom?: boolean }>`
  border-radius: 100%;
  width: 22px;
  height: 22px;
  padding: 0;
  display: flex;
  font-size: 10px;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.common.white};
  border: 2px solid ${({ theme }) => theme.colors.text.normal};
  position: fixed;
  z-index: 4;
  opacity: 0;
  pointer-events: none;

  ${({ bottom }) =>
    bottom &&
    css`
      position: absolute;
      bottom: -11px;
      right: 0;
    `}
`;

export const GroupContainer = styled.section<{
  dragged?: boolean;
  dropDisabled?: boolean;
  playbackCursorDragged?: boolean;
}>`
  margin-bottom: 18px;
  border: 2px solid ${({ theme }) => theme.colors.neutrals['92']};
  border-radius: 7px;
  position: relative;
  cursor: pointer;

  ${(props) =>
    props.dropDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    `}

  ${(props) =>
    props.playbackCursorDragged &&
    css`
      cursor: grab;
    `}

  &:after {
    height: 20px;
    width: 2px;
    background: ${({ theme }) => theme.colors.neutrals['92']};
    content: '';
    position: absolute;
    left: 50%;

    ${({ dragged }) =>
      dragged &&
      css`
        display: none;
      `}
  }

  &:last-of-type {
    margin-bottom: 20px;

    &:after {
      display: none;
    }
  }
`;

export const DragHandler = styled(DragHandlerBase)`
  opacity: 0;
  flex: none;
`;

export const GroupNameOverlay = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  width: 100%;

  > div {
    padding-bottom: 2px;
    flex: none;
    width: auto;
    max-width: calc(100% - 30px);
    overflow: hidden;
    text-overflow: ellipsis;
  }

  button {
    opacity: 0;
    margin-left: 4px;
  }

  button svg {
    transform: scale(0.8);
  }
`;

export const GroupNameContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: auto;

  flex: 230 0 auto;
  width: 230px;
  white-space: nowrap;
  text-overflow: ellipsis;

  > form {
    padding-right: 30px;
  }
`;

export const GroupHeader = styled.header<{ readOnly?: boolean }>`
  display: flex;
  flex: 1 0 auto;
  background: ${({ theme }) => theme.colors.background.neutral};
  font-size: 13px;
  line-height: 21px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  letter-spacing: 0.002em;
  overflow: visible;
  border: none !important;
  border-radius: 5px 5px 0 0;
  align-items: center;
  position: relative;
  top: -1px;

  &.is-pinned {
    z-index: 9;
    position: sticky;
    border-radius: 0;
    border-top: 3px solid ${({ theme }) => theme.colors.neutrals['92']} !important;
    box-shadow: 0px 15px 12px -17px #2d2d2d45;
  }

  &:hover {
    ${AddIconWrapper} {
      opacity: 1;

      ${({ readOnly }) =>
        readOnly &&
        css`
          opacity: 0;
        `}
    }

    ${DragHandler}, ${GroupNameContainer} button {
      opacity: 1;
    }
  }

  && {
    height: 44px;
  }
`;

export const GroupActions = styled(GroupActionsBase)`
  width: 40px;
  & > * {
    display: none;
  }

  ${GroupHeader}:hover & > * {
    display: flex;
  }
`;

export const GroupNameInput = styled(EditableTextBase)`
  padding: 0;
  margin-left: 0px;
  background: none;
  border-color: transparent;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
  font-size: inherit;
  color: inherit;
  font-weight: inherit;
  height: 30px;
`;

export const ExpanderContainer = styled.div`
  height: 100%;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale3d(0.8, 0.8, 1);
  width: 21px;
  margin-left: 5px;

  ${Expander} {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const StepsContainer = styled.div`
  width: 100%;
  overflow: visible;
  position: relative;
`;

export const SelectionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 32px;
  margin-left: -2px;
  flex: none;

  &:hover {
    background-color: rgba(29, 12, 36, 0.04);
  }
`;

export const AddIcon = styled(Icon).attrs({ name: 'add' })`
  font-size: 18px !important;
  fill: ${({ theme }) => theme.colors.text.normal} !important;
`;

export const GroupMetaWrapper = styled.div`
  display: flex;
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 55px;
  color: ${({ theme }) => theme.colors.text.secondaryAllCaps};
  padding-bottom: 2px;
`;

export const IntersectionDetection = styled.div``;

export const StepPlaceholder = styled.div`
  ${mixins.layout.flexCenter};
  justify-content: flex-start;
  gap: 10px;
  padding-left: 55px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutrals[94]};
`;

export const DropDisabledOverlay = styled.div`
  ${mixins.layout.flexCenter};
  justify-content: center;
  inset: 0;
  position: absolute;
  background: color-mix(
    in srgb,
    ${({ theme }) => theme.colors.background.neutral} 80%,
    transparent
  );
  z-index: 1;
  cursor: not-allowed;
`;
